import React, { useState, useEffect, useMemo, useRef } from 'react'
import ReactFlow, { ReactFlowProvider, Background, Controls } from 'react-flow-renderer'
import Loader from '../../../utilities/Loader'
import ApiActions from '../../../../actions/ApiActions'
import SnapshotDetail from './SnapshotDetail'
import SnapshotFaqs from './SnapshotFaqs'
import { makeNodeTypes, makeEdgeTypes, buildColorCodeOutcomes } from './canvasRenderers'

/*
 * Full-page read-only view of one snapshot, on its own route so it can use the
 * whole screen and so the browser tab makes it obvious this is not the live
 * script. Nothing here writes, and there is no restore: a restore run in this
 * tab would leave the builder tab showing the pre-restore script, and its next
 * node save would overwrite the restore. Restore lives in the Snapshots popup,
 * on the page that owns the builder's state.
 */

const formatCapturedAt = (value) => {
    if (!value) return ''
    return new Date(value).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
}

const SnapshotCanvas = (props) => {
    const teleproject = props.match.params.id
    const version = decodeURIComponent(props.match.params.version)

    const [scripts, setScripts] = useState(null)
    const [project, setProject] = useState(null)
    const [activeLanguage, setActiveLanguage] = useState('')
    const [selection, setSelection] = useState(null)
    const [showFaqs, setShowFaqs] = useState(false)
    const [error, setError] = useState('')

    //The capture time is in the object name, which is all this page is given.
    const capturedAt = useMemo(() => {
        const stamp = version.replace(/\.json$/, '').split('__')[0]
        const iso = stamp.replace(/^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z$/, '$1T$2:$3:$4.$5Z')
        const date = new Date(iso)
        return isNaN(date.getTime()) ? '' : date
    }, [version])

    const label = useMemo(() => {
        const parts = version.replace(/\.json$/, '').split('__')
        return parts.length > 1 ? parts.slice(1).join('__').replace(/-/g, ' ') : 'Unnamed snapshot'
    }, [version])

    useEffect(() => {
        let cancelled = false

        Promise.all([
            ApiActions.getSnapshot(teleproject, version),
            ApiActions.getTeleProject(teleproject)
        ])
            .then(([snapshotScripts, teleprojectData]) => {
                if (cancelled) return
                const list = Array.isArray(snapshotScripts) ? snapshotScripts : []
                setScripts(list)
                setProject(teleprojectData)
                const defaultScript = list.find(script => script.defaultScript) || list[0]
                setActiveLanguage(defaultScript ? defaultScript.scriptLanguage : '')
            })
            .catch(() => {
                if (!cancelled) setError('This snapshot could not be opened. It may no longer exist.')
            })

        return () => { cancelled = true }
    }, [teleproject, version])

    useEffect(() => {
        document.title = scripts ? `Snapshot - ${version}` : 'Snapshot'
    }, [scripts, version])

    const activeScript = scripts && scripts.find(script => script.scriptLanguage === activeLanguage)
    const elements = (activeScript && activeScript.script) || []
    //faqs are stored per language; the popup shows them all, with tabs.
    const hasAnyFaqs = (scripts || []).some(script => script.faqs && script.faqs.length > 0)

    //Colours are assigned by position, so this must match GraphDiagram exactly:
    //the project's outcome titles, then scriptHeadings from scripts[0] - the
    //FIRST script, not the active one. Seeding it per language shifts every
    //index and the whole palette comes out different from the builder's.
    const colorCodeOutcomes = useMemo(() => buildColorCodeOutcomes(
        (project && project.outcomes ? project.outcomes.map(outcome => outcome.title) : []),
        scripts && scripts[0] && scripts[0].scriptHeadings
    ), [project, scripts])

    //ReactFlow caches node/edge components from the first render, so these are
    //handed refs and built once - a rebuilt nodeTypes object is ignored, and a
    //closed-over array would stay stuck at its first (empty) value.
    const elementsRef = useRef(elements)
    const colorRef = useRef(colorCodeOutcomes)
    elementsRef.current = elements
    colorRef.current = colorCodeOutcomes

    //Selection is handled by ReactFlow's onElementClick rather than by handlers
    //inside the renderers: the custom node markup nests several divs, so a click
    //often lands on a child and never reaches the node's own onClick.
    const nodeTypes = useMemo(() => makeNodeTypes({ elementsRef, colorRef }), [])
    const edgeTypes = useMemo(() => makeEdgeTypes({ elementsRef, colorRef }), [])

    const onElementClick = (event, element) => {
        setSelection({ kind: element.source && element.target ? 'edge' : 'node', payload: element })
    }

    if (error) return <div className="snapshot-canvas-error">{error}</div>
    if (!scripts) return <Loader />

    return (
        <div className="snapshot-canvas-page">
            {/* Same header as the builder, minus Live and the action buttons -
                there is nothing to publish, share or delete from here. */}
            {/* The header mirrors the builder's, so the read-only marker has to
                carry real weight: an identical-looking tab is the hazard. It sits
                where the green Live dot does, with the snapshot it is showing. */}
            <div className="script-header snapshot-header">
                <div className="script-heaing">
                    <img src={require('../../../../assets/images/Icons_arrow.svg')}
                        onClick={() => window.close()} />
                    <h4>{project ? project.title : ''}</h4>
                    <div className="snapshot-chip">Read-only</div>
                </div>
                <div className="snapshot-canvas-right">
                    <div className="snapshot-canvas-meta">
                        <span className="name" title={label}>Snapshot name : {label}</span>
                        {capturedAt ? <span className="time">Snapshot captured on : {formatCapturedAt(capturedAt)}</span> : null}
                    </div>
                </div>
            </div>

            {/* i-wrap / flow are not decoration: every node colour, node shape
                and edge-path rule in tools.scss is nested inside .i-wrap, so a
                canvas rendered outside it comes out grey and unstyled. */}
            <div className="snapshot-canvas-body i-wrap">
                {/* The builder's toolbar without the node-creation cards - only
                    what a reader needs: FAQs and the language switcher. */}
                <div className="toolbar-script">
                    <div className="wrapper-i">
                        {hasAnyFaqs ?
                            <div className="card-wrap" onClick={() => setShowFaqs(true)}>
                                <div className="card-false">
                                    <img className="un-dragable" src={require('../../../../assets/images/int_script_toolbar/Icons_faqs.svg')} />
                                </div>
                                <div className="label-i">FAQs</div>
                            </div> : null}

                        <div className='language-listing'>
                            <p className='lang-header'>LANGUAGE</p>
                            {scripts.map(script => (
                                <div className={`list ${script.scriptLanguage == activeLanguage ? 'active' : ''}`}
                                    key={script.scriptLanguage}>
                                    <span className='name'
                                        onClick={() => { setActiveLanguage(script.scriptLanguage); setSelection(null) }}>
                                        {script.scriptLanguage}
                                    </span>
                                    <span className='default'>{script.defaultScript ? 'Default' : null}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>


                <div className="flow">
                    <ReactFlowProvider>
                        <ReactFlow
                            elements={elements}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            connectionLineType="step"
                            style={{ width: '100%', height: '100%' }}
                            //Dragging is allowed: it helps untangle a dense area,
                            //and nothing here can persist it - there is no
                            //onNodeDragStop, no save path and no ISContext.
                            //Connecting and deleting stay off: harmless, but they
                            //would make a backup look as though it had changed.
                            nodesDraggable={true}
                            nodesConnectable={false}
                            elementsSelectable={true}
                            onElementClick={onElementClick}
                            deleteKeyCode={null}
                            onLoad={(instance) => instance.fitView()}
                        >
                            <Background />
                            <Controls showInteractive={false} />
                        </ReactFlow>
                    </ReactFlowProvider>
                </div>

                <SnapshotDetail selection={selection} scripts={scripts} onClose={() => setSelection(null)} />

                {showFaqs ? <SnapshotFaqs show={showFaqs} scripts={scripts} closePopup={() => setShowFaqs(false)} /> : null}
            </div>
        </div>
    )
}

export default SnapshotCanvas
